import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import type { DictionaryEntry, SuggestionPayload, SuggestionType } from '../types'
import { SUGGESTION_TYPE_LABELS } from '../types'

interface Props {
  entry: DictionaryEntry
  open: boolean
  onClose: () => void
}

type FormState = Omit<SuggestionPayload, 'entry_id'>
type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error'

const EMPTY_FORM: FormState = {
  submitter_name:   '',
  submitter_email:  '',
  suggestion_type:  'better_magar',
  corrected_magar:  '',
  corrected_nepali: '',
  corrected_english:'',
  dialect_note:     '',
  speaker_notes:    '',
}

export function SuggestionModal({ entry, open, onClose }: Props) {
  const [form, setForm]         = useState<FormState>(EMPTY_FORM)
  const [status, setStatus]     = useState<SubmitStatus>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM)
      setStatus('idle')
      setErrorMsg('')
    }
  }, [open, entry.entry_id])

  const set = (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm(f => ({ ...f, [field]: e.target.value }))

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (status === 'submitting') return

    const hasContent =
      form.corrected_magar.trim() ||
      form.corrected_nepali.trim() ||
      form.corrected_english.trim() ||
      form.dialect_note.trim() ||
      form.speaker_notes.trim()

    if (!hasContent) {
      setErrorMsg('Please fill in at least one correction or note field.')
      return
    }

    setStatus('submitting')
    setErrorMsg('')

    const payload = {
      entry_id:          entry.entry_id,
      submitter_name:    form.submitter_name.trim()    || null,
      submitter_email:   form.submitter_email.trim()   || null,
      suggestion_type:   form.suggestion_type,
      corrected_magar:   form.corrected_magar.trim()   || null,
      corrected_nepali:  form.corrected_nepali.trim()  || null,
      corrected_english: form.corrected_english.trim() || null,
      dialect_note:      form.dialect_note.trim()      || null,
      speaker_notes:     form.speaker_notes.trim()     || null,
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await supabase.from('suggestions').insert(payload as any)

    if (error) {
      setStatus('error')
      setErrorMsg(error.message || 'Something went wrong. Please try again.')
    } else {
      setStatus('success')
    }
  }, [entry.entry_id, form, status])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/40 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      role="dialog"
      aria-modal="true"
      aria-label="Suggest a correction"
    >
      <div className="
        bg-white w-full sm:max-w-xl rounded-t-2xl sm:rounded-2xl shadow-2xl
        max-h-[92vh] flex flex-col overflow-hidden
      ">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-stone-100">
          <div>
            <h2 className="text-lg font-semibold text-stone-800">Suggest a Correction</h2>
            <p className="text-sm text-stone-500 mt-0.5 deva">
              {entry.magar_form_devanagari}
              {entry.magar_form_romanized && (
                <span className="not-italic font-sans text-stone-400 ml-2 text-xs">
                  ({entry.magar_form_romanized})
                </span>
              )}
              <span className="not-italic text-stone-400 font-sans mx-2">↔</span>
              {entry.nepali_form_devanagari}
            </p>
          </div>
          <button
            onClick={onClose}
            className="btn-ghost mt-0.5"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Success state */}
        {status === 'success' ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 gap-4 text-center">
            <div className="text-4xl">🙏</div>
            <h3 className="text-xl font-semibold text-stone-800">Thank you!</h3>
            <p className="text-stone-600">
              Your suggestion has been recorded and will be reviewed.
              Community contributions like yours help improve this dictionary for Magar speakers everywhere.
            </p>
            <button className="btn-primary mt-2" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">

            {/* Suggestion type */}
            <Field label="Type of suggestion" required>
              <select
                value={form.suggestion_type}
                onChange={set('suggestion_type')}
                className="form-select w-full rounded-lg border-stone-300 text-sm
                           focus:ring-magar-600 focus:border-magar-600"
              >
                {(Object.entries(SUGGESTION_TYPE_LABELS) as [SuggestionType, string][]).map(
                  ([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  )
                )}
              </select>
            </Field>

            {/* Correction fields */}
            <div className="rounded-xl bg-stone-50 border border-stone-100 p-4 space-y-3">
              <p className="text-xs text-stone-500 font-semibold uppercase tracking-wide">
                Corrections (fill only what you know)
              </p>

              <Field label="Corrected Magar form">
                <DevaInput
                  value={form.corrected_magar}
                  onChange={set('corrected_magar')}
                  placeholder="Devanagari or Roman Magar"
                />
              </Field>

              <Field label="Corrected Nepali form">
                <DevaInput
                  value={form.corrected_nepali}
                  onChange={set('corrected_nepali')}
                  placeholder="Devanagari or Roman Nepali"
                />
              </Field>

              <Field label="Corrected English gloss">
                <input
                  type="text"
                  value={form.corrected_english}
                  onChange={set('corrected_english')}
                  placeholder="e.g. I, me (1st person singular)"
                  className="form-input w-full rounded-lg border-stone-300 text-sm
                             focus:ring-magar-600 focus:border-magar-600"
                />
              </Field>
            </div>

            {/* Dialect / notes */}
            <Field label="Dialect note">
              <DevaInput
                value={form.dialect_note}
                onChange={set('dialect_note')}
                placeholder="e.g. In Palpa dialect this is written as…"
                multiline
              />
            </Field>

            <Field label="Notes for reviewer">
              <textarea
                value={form.speaker_notes}
                onChange={set('speaker_notes')}
                placeholder="Any other context — why this is wrong, how to improve it, etc."
                rows={3}
                className="form-textarea w-full rounded-lg border-stone-300 text-sm
                           focus:ring-magar-600 focus:border-magar-600"
              />
            </Field>

            {/* Submitter info (optional) */}
            <div className="rounded-xl bg-stone-50 border border-stone-100 p-4 space-y-3">
              <p className="text-xs text-stone-500 font-semibold uppercase tracking-wide">
                About you (optional)
              </p>
              <Field label="Your name">
                <input
                  type="text"
                  value={form.submitter_name}
                  onChange={set('submitter_name')}
                  placeholder="Anonymous if left blank"
                  className="form-input w-full rounded-lg border-stone-300 text-sm
                             focus:ring-magar-600 focus:border-magar-600"
                />
              </Field>
              <Field label="Your email">
                <input
                  type="email"
                  value={form.submitter_email}
                  onChange={set('submitter_email')}
                  placeholder="We'll only use this to follow up on your suggestion"
                  className="form-input w-full rounded-lg border-stone-300 text-sm
                             focus:ring-magar-600 focus:border-magar-600"
                />
              </Field>
            </div>

            {/* Error */}
            {(status === 'error' || errorMsg) && (
              <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                {errorMsg || 'Something went wrong. Please try again.'}
              </p>
            )}

            {/* Actions */}
            <div className="flex gap-3 justify-end pt-2">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? 'Submitting…' : 'Submit Suggestion'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

function Field({
  label,
  required,
  children,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-stone-700">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
    </div>
  )
}

function DevaInput({
  value,
  onChange,
  placeholder,
  multiline,
}: {
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  placeholder?: string
  multiline?: boolean
}) {
  const cls = `
    deva w-full rounded-lg border-stone-300 text-sm
    focus:ring-magar-600 focus:border-magar-600
  `
  return multiline ? (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={2}
      className={`form-textarea ${cls}`}
    />
  ) : (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`form-input ${cls}`}
    />
  )
}
